/**
 * The social panels the old ReportDisplay drew inline, one component each
 * so the Reporting canvas can put any of them in the rail or a tile:
 * Overview, Performance Trends, Booking Intent and Content Insights.
 */

import React from "react";
import { SocialMediaReport, ReportComparison, TrendDataPoint } from "../hooks/useSocialMediaReport";
import {
  ChangePill,
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
  ReportStats,
} from "./ReportPanel";
import {
  MonthColumns,
  MonthLabels,
  QuietList,
  SOURCE_COLOR,
  SourceBadge,
  Sparkline,
  SplitBar,
  VizHeading,
  type MonthColumn,
} from "./ReportViz";
import { TrendsChart } from "./TrendsChart";
import { ContentInsights } from "./ContentInsights";
import { ConversionTrendChart } from "./ConversionTrendChart";
import { MONTH_SHORT, proRataLabel } from "../utils/metricChange";
import { useMonthAdAnalytics } from "@/domains/Ads/hooks/useMonthAdAnalytics";
import { ChartBarIcon, HeartIcon, LightbulbIcon, LinkIcon } from "@/components/icons";
import { PLATFORM_REGISTRY } from "@/config/platforms";

export interface SocialPanelProps {
  report: SocialMediaReport;
  comparison?: ReportComparison | null;
  historicalData: TrendDataPoint[];
  clientId: string;
}

const badge = (Icon: React.ComponentType<{ className?: string }>, color: string) => (
  <SourceBadge color={color}>
    <Icon className="w-3.5 h-3.5" />
  </SourceBadge>
);

/** Each panel's badge, which a window's by-month notice reuses. */
export const SOCIAL_PANEL_ICON = {
  overview: badge(ChartBarIcon, SOURCE_COLOR.social),
  trends: badge(HeartIcon, SOURCE_COLOR.social),
  booking: badge(LinkIcon, PLATFORM_REGISTRY.instagram?.color ?? SOURCE_COLOR.social),
  content: badge(LightbulbIcon, SOURCE_COLOR.website),
};

export const OverviewPanel: React.FC<SocialPanelProps> = ({ report, comparison, historicalData, clientId }) => {
  // Organic vs paid overview: paid = this month's (link-click era) ad
  // impressions; organic = combined minus paid — same model as the idea
  // Impact panel.
  const { current: monthAds } = useMonthAdAnalytics(clientId, report.year, report.month);
  const paidImpressions = monthAds?.totals.impressions ?? 0;
  const organicImpressions = Math.max(0, report.combined.totalImpressions - paidImpressions);
  const boostedCampaigns = monthAds?.campaignCount ?? 0;
  const isReportMonth = (d: TrendDataPoint) => d.year === report.year && d.month === report.month;
  // A month the report covers in part reads "to date" on hover, so its
  // shorter column is never taken for a fall.
  const monthName = (d: TrendDataPoint) =>
    `${MONTH_SHORT[d.month - 1]}${isReportMonth(d) && comparison?.proRata ? " to date" : ""}`;
  const impressionColumns: MonthColumn[] = historicalData.map((d) => ({
    key: `${d.year}-${d.month}`,
    label: MONTH_SHORT[d.month - 1],
    value: d.totalImpressions,
    current: isReportMonth(d),
    title: `${monthName(d)}: ${d.totalImpressions.toLocaleString()} impressions`,
  }));
  return (
    <ReportPanel
      title="Overview"
      icon={SOCIAL_PANEL_ICON.overview}
      summary={comparison?.proRata ? proRataLabel(comparison.proRata) : undefined}
      headlineLabel="Impressions"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Impressions"
              value={report.combined.totalImpressions.toLocaleString()}
              change={comparison?.changes.combined.impressions}
            />
          }
          chart={
            <Sparkline
              values={historicalData.map((d) => d.totalImpressions)}
              color={SOURCE_COLOR.social}
              title={`Impressions by month, last ${historicalData.length} months`}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {historicalData.length > 0 && (
          <div>
            <VizHeading>Impressions by month</VizHeading>
            <MonthColumns columns={impressionColumns} color={SOURCE_COLOR.social} />
            <MonthLabels columns={impressionColumns} />
          </div>
        )}
        <ReportStats>
          <ReportStat
            label="Impressions"
            value={report.combined.totalImpressions.toLocaleString()}
            change={comparison?.changes.combined.impressions}
          />
          <ReportStat
            label="Reach"
            value={report.combined.totalReach.toLocaleString()}
            change={comparison?.changes.combined.reach ?? undefined}
          />
          <ReportStat
            label="Engagement"
            value={report.combined.totalEngagement.toLocaleString()}
            change={comparison?.changes.combined.engagement}
          />
        </ReportStats>
        <QuietList
          rows={[
            {
              label: boostedCampaigns > 0 ? `Posts · ${boostedCampaigns} boosted` : "Posts · all organic",
              value: report.combined.totalPosts.toLocaleString(),
              aside: comparison ? <ChangePill change={comparison.changes.combined.posts} /> : undefined,
            },
          ]}
        />
        {/* Paid stays apart from organic: amber, as on every paid panel. */}
        {paidImpressions > 0 && report.combined.totalImpressions > 0 && (
          <div>
            <VizHeading>Impressions, organic vs paid</VizHeading>
            <SplitBar
              parts={[
                { label: "Organic", value: organicImpressions, color: SOURCE_COLOR.social },
                { label: "Paid", value: paidImpressions, color: SOURCE_COLOR.paid },
              ]}
            />
          </div>
        )}
      </div>
    </ReportPanel>
  );
};

export const TrendsPanel: React.FC<SocialPanelProps> = ({ report, comparison, historicalData }) => {
  const hasTrends = historicalData.length > 0;
  return (
    <ReportPanel
      title="Performance Trends"
      icon={SOCIAL_PANEL_ICON.trends}
      summary={hasTrends ? `last ${historicalData.length} months · K` : undefined}
      headlineLabel={hasTrends ? "Engagement" : undefined}
      collapsedBody={
        hasTrends ? (
          <ReportHeadline
            stat={
              <ReportStat
                size="lg"
                label="Engagement"
                value={report.combined.totalEngagement.toLocaleString()}
                change={comparison?.changes.combined.engagement}
              />
            }
            chart={
              <Sparkline
                values={historicalData.map((d) => d.totalEngagement)}
                color={SOURCE_COLOR.social}
                title={`Engagement by month, last ${historicalData.length} months`}
              />
            }
          />
        ) : (
          <ReportPanelNote>No trend history yet.</ReportPanelNote>
        )
      }
    >
      {hasTrends ? (
        <TrendsChart historicalData={historicalData} />
      ) : (
        <ReportPanelEmpty
          message="No trend history yet."
          hint="Trends appear once at least one earlier monthly report exists."
        />
      )}
    </ReportPanel>
  );
};

export const BookingIntentPanel: React.FC<SocialPanelProps> = ({ report, historicalData }) => {
  const hasTrends = historicalData.length > 0;
  const color = PLATFORM_REGISTRY.instagram?.color ?? SOURCE_COLOR.social;
  // Instagram link taps and website clicks: organic only, paid stays on its
  // own line in the expanded chart (DD-021).
  const igClicks = (d: TrendDataPoint): number | null =>
    d.instagramConversion ? d.instagramConversion.profileLinksTaps + d.instagramConversion.websiteClicks : null;
  const thisMonthIgClicks = report.instagram
    ? (report.instagram.totalProfileLinksTaps ?? 0) + (report.instagram.totalWebsiteClicks ?? 0)
    : null;
  return (
    <ReportPanel
      title="Booking Intent"
      icon={SOCIAL_PANEL_ICON.booking}
      summary={hasTrends ? "Instagram link taps & clicks" : undefined}
      headlineLabel={hasTrends && thisMonthIgClicks != null ? "Link taps and website clicks" : undefined}
      collapsedBody={
        !hasTrends ? (
          <ReportPanelNote>No booking-intent history yet.</ReportPanelNote>
        ) : thisMonthIgClicks != null ? (
          <ReportHeadline
            stat={
              <ReportStat
                size="lg"
                label="Link taps and website clicks"
                value={thisMonthIgClicks.toLocaleString()}
                note="this month"
              />
            }
            chart={
              <Sparkline
                values={historicalData.map(igClicks)}
                color={color}
                title={`Instagram link taps and website clicks by month, last ${historicalData.length} months`}
              />
            }
          />
        ) : (
          <ReportPanelNote>No Instagram data this month.</ReportPanelNote>
        )
      }
    >
      {hasTrends ? (
        <ConversionTrendChart historicalData={historicalData} />
      ) : (
        <ReportPanelEmpty
          message="No booking-intent history yet."
          hint="Link taps and website clicks chart from the second monthly report on."
        />
      )}
    </ReportPanel>
  );
};

export const ContentInsightsPanel: React.FC<SocialPanelProps> = ({ report }) => {
  const insights = report.contentInsights;
  const bestType = insights?.byPostType.length
    ? insights.byPostType.reduce((best, t) => (t.avgEngagement > best.avgEngagement ? t : best))
    : null;
  return (
    <ReportPanel
      title="Content Insights"
      icon={SOCIAL_PANEL_ICON.content}
      summary={insights ? `${insights.totalMatched} posts matched` : undefined}
      headlineLabel={bestType ? "Best post type" : undefined}
      collapsedBody={
        bestType ? (
          <ReportStat
            size="lg"
            label="Best post type"
            value={bestType.postType}
            note={`avg ${Math.round(bestType.avgEngagement).toLocaleString()} engagement`}
          />
        ) : (
          <ReportPanelNote>No content insights for this month.</ReportPanelNote>
        )
      }
    >
      {insights ? (
        <ContentInsights insights={insights} />
      ) : (
        <ReportPanelEmpty
          message="No content insights for this month."
          hint="Insights appear when published posts can be matched to their platform metrics."
        />
      )}
    </ReportPanel>
  );
};
